/**
 * Typo-tolerant team-name search for the scoring rail's jump box.
 *
 * The host is typing a team name off a paper scoresheet, mid-round, under
 * time pressure — so "brian bru" has to find "Brain Brew Crew". Matching
 * runs over the event's already-loaded teams (tens of rows), so this is a
 * plain in-memory scan; see matchTeams for the ranking.
 *
 * Normalization mirrors Cerebrex/scripts/seedCanonicalTeams.js so the search
 * agrees with how that script clusters names into canonical teams.
 */

const NUMW: Record<string, string> = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5',
  six: '6', seven: '7', eight: '8', nine: '9', ten: '10',
};

/** Lowercase, &→and, strip punctuation, spell out digits, drop "the". */
export function norm(s: string): string {
  let x = s.toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9\s]/g, ' ');
  x = x.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine|ten)\b/g, (m) => NUMW[m]!);
  return x.replace(/\bthe\b/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Damerau-Levenshtein — counts an adjacent transposition as one edit, not
 * two. Matters here because swapped letters ("brian"/"brain") are the most
 * common way a name gets mistyped, and plain Levenshtein scores them as
 * badly as two unrelated substitutions.
 */
function lev(a: string, b: string): number {
  const m = a.length, n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev2: number[] = [];
  let prev: number[] = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur: number[] = [i];
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(prev[j]! + 1, cur[j - 1]! + 1, prev[j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        cur[j] = Math.min(cur[j]!, prev2[j - 2]! + 1);
      }
    }
    prev2 = prev;
    prev = cur;
  }
  return prev[n]!;
}

// ponytail: an edit budget, not a similarity threshold. A ratio cutoff has to
// be lenient enough for a 1-typo 3-letter token ("bru"), which then makes it
// far too lenient for long ones. Edits-allowed scales the way typos actually
// do. At <=2 chars the budget is 0 — everything is within one edit of
// everything down there, and fuzzy would just flood the list.
const maxEdits = (len: number): number => (len <= 2 ? 0 : len <= 4 ? 1 : len <= 7 ? 2 : 3);

/**
 * Best similarity of one query token against any word in the name, or 0 for
 * no match. Compares against the leading slice of the name word so a
 * half-typed word ("bru" mid-way to "brew") is judged on what was typed, not
 * penalised for the letters the host hasn't reached yet.
 */
function tokenScore(qt: string, nameTokens: string[]): number {
  const budget = maxEdits(qt.length);
  let best = 0;
  for (const nt of nameTokens) {
    if (nt.startsWith(qt)) return 1;
    if (budget === 0) continue;
    const d = lev(qt, nt.slice(0, qt.length));
    if (d <= budget) best = Math.max(best, 1 - d / qt.length);
  }
  return best;
}

export interface Named { name: string }

export interface TeamMatch<T extends Named> {
  team: T;
  /** 3 = name starts with the query, 2 = contains it, 1 = fuzzy. */
  tier: 3 | 2 | 1;
  /** Case-insensitive [start, end) of the raw query inside the raw name, for highlighting. */
  span: [number, number] | null;
}

/**
 * Teams matching `query`, best first.
 *
 * Ranked so the old prefix behaviour still wins outright: typing two letters
 * puts the literal prefix match at the top (where Enter picks it up), and
 * typo-tolerant matches only fill in below it.
 */
function rawSubstringMatches<T extends Named>(teams: readonly T[], raw: string): TeamMatch<T>[] {
  const needle = raw.toLowerCase();
  const out: TeamMatch<T>[] = [];
  for (const team of teams) {
    const at = team.name.toLowerCase().indexOf(needle);
    if (at !== -1) out.push({ team, tier: 2, span: [at, at + raw.length] });
  }
  return out;
}

export function matchTeams<T extends Named>(teams: readonly T[], query: string): TeamMatch<T>[] {
  const raw = query.trim();
  if (!raw) return [];
  const q = norm(raw);
  // norm() strips "the" and punctuation, so a query made only of those
  // ("the", "&") normalises away to nothing. Fall back to a literal
  // substring search rather than telling the host there are no matches.
  if (!q) return rawSubstringMatches(teams, raw);
  const qTokens = q.split(' ');

  const scored: { m: TeamMatch<T>; score: number; i: number }[] = [];

  teams.forEach((team, i) => {
    const n = norm(team.name);
    const nameTokens = n.split(' ').filter(Boolean);

    let tier: 3 | 2 | 1 | 0 = 0;
    let score = 0;

    // Compare with spaces removed as well as with them: hosts typing off a
    // paper sheet split and join words differently than whoever registered
    // the team ("smartypants" for "smarty pants"), and per-token matching
    // can't bridge that on its own.
    const nFlat = n.replace(/ /g, '');
    const qFlat = q.replace(/ /g, '');

    if (n.startsWith(q) || nFlat.startsWith(qFlat)) {
      tier = 3;
      score = 1;
    } else if (q.length > 1 && (n.includes(q) || nFlat.includes(qFlat))) {
      // Single characters stay prefix-only: on a real venue's team list a
      // one-letter substring matches about two thirds of the rail, which is
      // no more useful than showing everything.
      tier = 2;
      score = 1;
    } else {
      // Every token the host typed has to land on some word in the name;
      // one stray token means it's a different team, not a typo.
      const perToken = qTokens.map((qt) => tokenScore(qt, nameTokens));
      if (perToken.every((s) => s > 0)) {
        tier = 1;
        score = perToken.reduce((a, b) => a + b, 0) / perToken.length;
      }
    }

    if (tier === 0) return;

    const at = team.name.toLowerCase().indexOf(raw.toLowerCase());
    scored.push({
      m: { team, tier, span: at === -1 ? null : [at, at + raw.length] },
      score,
      i,
    });
  });

  scored.sort((a, b) => b.m.tier - a.m.tier || b.score - a.score || a.i - b.i);
  return scored.map((s) => s.m);
}
